import { useState } from "react";
import {
  useGetDashboardSummary,
  useGetRecentEstimates,
  useGetEstimatesByStatus,
  useGetProfitPerEstimate,
  useGetInvoiceDashboardSummary,
  useGetInvoiceDashboardRevenueByCategory,
  useListInvoices,
  getListEstimatesQueryKey,
  getListInvoicesQueryKey,
} from "@workspace/api-client-react";
import { useSettingsFormat } from "@/hooks/use-settings-format";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  CheckCircle, LayoutDashboard,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Cell, LineChart, Line, LabelList,
  PieChart, Pie,
} from "recharts";
import TodoWidget from "@/components/TodoWidget";
import { InfoTip } from "@/components/ui/info-tip";
import { FORMULAS } from "@/lib/formulas";

const STATUS_ORDER = ["draft", "sent", "accepted", "declined", "converted"];

const STATUS_COLORS: Record<string, string> = {
  draft: "#94a3b8",
  sent: "#3b82f6",
  accepted: "#22c55e",
  declined: "#ef4444",
  converted: "#a855f7",
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  sent: "Sent",
  accepted: "Accepted",
  declined: "Declined",
  converted: "Converted",
};

const getInvoiceStatusColor = (status: string) => {
  switch (status) {
    case "paid": return "bg-green-100 text-green-800 border-green-300";
    case "sent": return "bg-blue-100 text-blue-800 border-blue-300";
    case "overdue": return "bg-red-100 text-red-800 border-red-300";
    case "void": return "bg-gray-200 text-gray-600 border-gray-300";
    default: return "bg-slate-100 text-slate-700 border-slate-300";
  }
};

type DateRange = "7" | "30" | "90" | "custom" | "all";

function getDateRange(range: DateRange, customStart?: string, customEnd?: string): { startDate?: string; endDate?: string } {
  if (range === "all") return {};
  if (range === "custom") {
    return { startDate: customStart, endDate: customEnd };
  }
  const days = parseInt(range, 10);
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

const rangeButtons: { label: string; value: DateRange }[] = [
  { label: "All Time", value: "all" },
  { label: "Past 7 Days", value: "7" },
  { label: "Past 30 Days", value: "30" },
  { label: "Past 90 Days", value: "90" },
  { label: "Custom", value: "custom" },
];

export default function CombinedDashboard() {
  usePageTitle("Dashboard");

  const [activeRange, setActiveRange] = useState<DateRange>("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [appliedCustomStart, setAppliedCustomStart] = useState("");
  const [appliedCustomEnd, setAppliedCustomEnd] = useState("");

  const dateParams = getDateRange(
    activeRange,
    appliedCustomStart || undefined,
    appliedCustomEnd || undefined,
  );
  const hasDateParams = Object.keys(dateParams).length > 0;

  const { data: quoteSummary, isLoading: isLoadingQuoteSummary } = useGetDashboardSummary(
    hasDateParams ? dateParams : undefined,
  );
  const { data: invoiceSummary, isLoading: isLoadingInvoiceSummary } = useGetInvoiceDashboardSummary(
    hasDateParams ? dateParams : undefined,
  );
  const { data: revenueByCategory, isLoading: isLoadingRevenueByCategory } = useGetInvoiceDashboardRevenueByCategory(
    hasDateParams ? dateParams : undefined,
  );
  const { data: recentEstimates, isLoading: isLoadingRecent } = useGetRecentEstimates(
    { limit: 5, ...dateParams },
  );
  const { data: statusCounts, isLoading: isLoadingStatus } = useGetEstimatesByStatus(
    hasDateParams ? dateParams : undefined,
  );
  const { data: profitData, isLoading: isLoadingProfit } = useGetProfitPerEstimate(
    hasDateParams ? dateParams : undefined,
  );
  const { data: sentInvoices, isLoading: isLoadingSent } = useListInvoices(
    { status: "sent" as const, ...(hasDateParams ? dateParams : {}) },
  );
  const { data: overdueInvoices, isLoading: isLoadingOverdue } = useListInvoices(
    { status: "overdue" as const, ...(hasDateParams ? dateParams : {}) },
  );

  const { formatCurrency: formatCurrencyFromSettings, formatDate } = useSettingsFormat();

  const formatCurrency = formatCurrencyFromSettings;

  const outstanding = (() => {
    const combined = [...(sentInvoices ?? []), ...(overdueInvoices ?? [])];
    const seen = new Set<number>();
    return combined.filter(inv => {
      if (seen.has(inv.id)) return false;
      seen.add(inv.id);
      return true;
    });
  })();

  const sortedStatusData = STATUS_ORDER
    .map(s => (statusCounts || []).find(d => d.status === s))
    .filter((d): d is NonNullable<typeof d> => !!d);

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "accepted": return "bg-green-100 text-green-800 border-green-300";
      case "sent": return "bg-blue-100 text-blue-800 border-blue-300";
      case "declined": return "bg-red-100 text-red-800 border-red-300";
      case "converted": return "bg-purple-100 text-purple-800 border-purple-200";
      default: return "bg-slate-100 text-slate-700 border-slate-300";
    }
  };

  function handleApplyCustom() {
    setAppliedCustomStart(customStart);
    setAppliedCustomEnd(customEnd);
  }

  const subheaderStyle = {
    fontSize: "1.125rem",
    fontWeight: 600,
    color: "hsl(var(--foreground))",
    borderBottom: "2px solid hsl(var(--border))",
    paddingBottom: "0.5rem",
    marginBottom: "0.75rem",
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <LayoutDashboard className="w-8 h-8 text-primary" />
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Your quoting and invoicing pipeline at a glance.</p>
        </div>
        <div className="flex gap-2">
          <Link href="/quotes/new">
            <Button variant="outline">New Quote</Button>
          </Link>
          <Link href="/invoices/new">
            <Button>New Invoice</Button>
          </Link>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {rangeButtons.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setActiveRange(value)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeRange === value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:border-primary hover:text-primary"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {activeRange === "custom" && (() => {
          const invalidOrder = !!customStart && !!customEnd && customStart > customEnd;
          return (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="date"
                value={customStart}
                max={customEnd || undefined}
                onChange={e => setCustomStart(e.target.value)}
                className="w-40 h-8 text-sm"
                placeholder="Start date"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                onChange={e => setCustomEnd(e.target.value)}
                className="w-40 h-8 text-sm"
                placeholder="End date"
              />
              <Button
                size="sm"
                onClick={handleApplyCustom}
                disabled={!customStart || !customEnd || invalidOrder}
              >
                Apply
              </Button>
              {invalidOrder && (
                <span className="text-xs text-red-600">Start date must be before end date.</span>
              )}
            </div>
          );
        })()}
      </div>

      {/* KPI Grid — full width */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card className="shadow-sm border-border/50" style={{ borderTop: "3px solid hsl(38 90% 55%)" }}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
            <InfoTip content={FORMULAS.dashTotalValue} />
          </CardHeader>
          <CardContent>
            {isLoadingQuoteSummary ? <Skeleton className="h-8 w-24" /> : (
              <div className="text-2xl font-bold">{formatCurrency(quoteSummary?.totalValue || 0)}</div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm border-border/50" style={{ borderTop: "3px solid #22c55e" }}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Accepted Value</CardTitle>
            <InfoTip content={FORMULAS.dashAcceptedValue} />
          </CardHeader>
          <CardContent>
            {isLoadingQuoteSummary ? <Skeleton className="h-8 w-24" /> : (
              <div className="text-2xl font-bold text-green-700">{formatCurrency(quoteSummary?.acceptedValue || 0)}</div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm border-border/50" style={{ borderTop: "3px solid #10b981" }}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Accepted Profit</CardTitle>
            <InfoTip content={FORMULAS.dashAcceptedProfit} />
          </CardHeader>
          <CardContent>
            {isLoadingQuoteSummary ? <Skeleton className="h-8 w-24" /> : (
              <div className="text-2xl font-bold text-emerald-700">{formatCurrency(quoteSummary?.acceptedProfit || 0)}</div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm border-border/50" style={{ borderTop: "3px solid #3b82f6" }}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending Value</CardTitle>
            <InfoTip content={FORMULAS.dashPendingValue} />
          </CardHeader>
          <CardContent>
            {isLoadingQuoteSummary ? <Skeleton className="h-8 w-24" /> : (
              <div className="text-2xl font-bold text-blue-700">{formatCurrency(quoteSummary?.pendingValue || 0)}</div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm border-border/50" style={{ borderTop: "3px solid #a855f7" }}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Acceptance Rate</CardTitle>
            <InfoTip content={FORMULAS.dashAcceptanceRate} />
          </CardHeader>
          <CardContent>
            {isLoadingQuoteSummary ? <Skeleton className="h-8 w-24" /> : (
              <div className="text-2xl font-bold">{(quoteSummary?.acceptanceRate || 0).toFixed(1)}%</div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm border-border/50" style={{ borderTop: "3px solid #14b8a6" }}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Average Margin</CardTitle>
            <InfoTip content={FORMULAS.dashAverageMargin} />
          </CardHeader>
          <CardContent>
            {isLoadingQuoteSummary ? <Skeleton className="h-8 w-24" /> : (
              <div className="text-2xl font-bold">{(quoteSummary?.averageMargin || 0).toFixed(1)}%</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* To-Do Widget */}
      <TodoWidget />

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left column — Quotes */}
        <div className="space-y-6">
          <p style={subheaderStyle}>Quotes</p>

          <Card className="shadow-sm border-border/50">
            <CardHeader>
              <CardTitle>Quotes by Status</CardTitle>
              <CardDescription>Breakdown of your current pipeline by status</CardDescription>
            </CardHeader>
            <CardContent className="h-56">
              {isLoadingStatus ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Skeleton className="w-full h-full" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sortedStatusData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis
                      dataKey="status"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => STATUS_LABELS[v] ?? v}
                      style={{ fontSize: "0.75rem", fontWeight: 500 }}
                    />
                    <RechartsTooltip
                      cursor={{ fill: "#f1f5f9" }}
                      contentStyle={{ borderRadius: "6px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                      formatter={(value, _name, props) => [value, STATUS_LABELS[props.payload.status] ?? props.payload.status]}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={64}>
                      {sortedStatusData.map((entry) => (
                        <Cell key={entry.status} fill={STATUS_COLORS[entry.status] ?? "#94a3b8"} />
                      ))}
                      <LabelList dataKey="count" position="insideBottom" offset={6} style={{ fontSize: "0.7rem", fontWeight: 600, fill: "#fff" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm border-border/50">
            <CardHeader>
              <CardTitle>Margin per Quote</CardTitle>
              <CardDescription>Margin percentage for each quote over time</CardDescription>
            </CardHeader>
            <CardContent className="h-56">
              {isLoadingProfit ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Skeleton className="w-full h-full" />
                </div>
              ) : !profitData || profitData.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                  Create a quote to start seeing data.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={profitData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => formatDate(v, { month: "short", day: "numeric" })}
                      style={{ fontSize: "0.75rem" }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      style={{ fontSize: "0.75rem" }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <RechartsTooltip
                      contentStyle={{ borderRadius: "6px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                      formatter={(value: number, _name, props) => [
                        `${Number(value).toFixed(2)}%`,
                        props.payload.estimateNumber,
                      ]}
                      labelFormatter={() => ""}
                    />
                    <Line
                      type="linear"
                      dataKey="margin"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ r: 4, fill: "#3b82f6", strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: "#3b82f6" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Recent Quotes */}
          <Card className="shadow-sm border-border/50">
            <CardHeader>
              <CardTitle>Recent Quotes</CardTitle>
              <CardDescription>Latest quotes created</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {isLoadingRecent ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    </div>
                  ))
                ) : !recentEstimates || recentEstimates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">No recent quotes found.</div>
                ) : (
                  recentEstimates.map((estimate) => (
                    <Link key={estimate.id} href={`/quotes/${estimate.id}`}>
                      <div className="flex items-center justify-between p-3 -mx-3 rounded-md hover:bg-muted/50 cursor-pointer transition-colors group">
                        <div className="flex flex-col gap-1 min-w-0 pr-4">
                          <span className="text-sm font-medium truncate group-hover:text-primary transition-colors">{estimate.title}</span>
                          <span className="text-xs text-muted-foreground truncate">{estimate.customerName}</span>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className="text-sm font-semibold">{formatCurrency(estimate.total)}</span>
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getStatusBadgeColor(estimate.status)}`}>
                            {STATUS_LABELS[estimate.status] ?? estimate.status}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <Link href="/quotes" className="text-sm text-primary hover:underline font-medium flex items-center justify-center w-full">
                  View all quotes
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column — Invoices */}
        <div className="space-y-6">
          <p style={subheaderStyle}>Invoices</p>

          {/* Invoice Status */}
          <Card className="shadow-sm border-border/50">
            <CardHeader className="pb-2">
              <CardTitle>Invoice Status</CardTitle>
              <CardDescription>Paid, outstanding, and overdue breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingInvoiceSummary ? (
                <div className="flex flex-col items-center gap-4">
                  <Skeleton className="h-36 w-36 rounded-full" />
                  <div className="space-y-2 w-full">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ) : (() => {
                const paid = invoiceSummary?.totalPaid ?? 0;
                const outstanding = (invoiceSummary?.totalOutstanding ?? 0) - (invoiceSummary?.totalOverdue ?? 0);
                const overdue = invoiceSummary?.totalOverdue ?? 0;
                const total = invoiceSummary?.totalInvoiced ?? 0;
                const allZero = paid === 0 && outstanding === 0 && overdue === 0;
                const statusData = allZero
                  ? [{ name: "No data", value: 1, color: "#e2e8f0" }]
                  : [
                      { name: "Paid", value: paid, color: "#22c55e" },
                      { name: "Outstanding", value: Math.max(outstanding, 0), color: "#3b82f6" },
                      { name: "Overdue", value: overdue, color: "#ef4444" },
                    ].filter(d => d.value > 0);
                return (
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative">
                      <ResponsiveContainer width={160} height={160}>
                        <PieChart>
                          <Pie
                            data={statusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={52}
                            outerRadius={72}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                            strokeWidth={0}
                          >
                            {statusData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-base font-bold leading-tight">
                          {allZero ? "$0" : formatCurrency(total)}
                        </span>
                        <span className="text-[10px] text-muted-foreground">Total</span>
                      </div>
                    </div>
                    <div className="w-full space-y-1.5">
                      {allZero ? (
                        <p className="text-xs text-muted-foreground text-center">No invoice data yet.</p>
                      ) : (
                        <>
                          {paid > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-1.5">
                                <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500 flex-shrink-0" />
                                <span className="text-muted-foreground">Paid</span>
                              </div>
                              <span className="font-medium">{formatCurrency(paid)}</span>
                            </div>
                          )}
                          {outstanding > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-1.5">
                                <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-500 flex-shrink-0" />
                                <span className="text-muted-foreground">Outstanding</span>
                              </div>
                              <span className="font-medium">{formatCurrency(outstanding)}</span>
                            </div>
                          )}
                          {overdue > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-1.5">
                                <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500 flex-shrink-0" />
                                <span className="text-muted-foreground">Overdue</span>
                              </div>
                              <span className="font-medium">{formatCurrency(overdue)}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Chart 2 — Revenue by Service */}
          <Card className="shadow-sm border-border/50">
            <CardHeader className="pb-2">
              <CardTitle>Revenue by Service</CardTitle>
              <CardDescription>Line item revenue across all invoices by category</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingRevenueByCategory ? (
                <div className="flex flex-col items-center gap-4">
                  <Skeleton className="h-36 w-36 rounded-full" />
                  <div className="space-y-2 w-full">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ) : (() => {
                const CATEGORY_COLORS = [
                  "#6366f1", "#f59e0b", "#10b981", "#ec4899", "#8b5cf6",
                  "#14b8a6", "#f97316", "#06b6d4", "#84cc16", "#a78bfa",
                ];
                const data = revenueByCategory ?? [];
                const allZero = data.length === 0 || data.every(d => d.total === 0);
                const categoryCount = data.length;
                const chartData = allZero
                  ? [{ name: "No data", value: 1, color: "#e2e8f0" }]
                  : data.map((d, i) => ({
                      name: d.category,
                      value: d.total,
                      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
                    }));
                return (
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative">
                      <ResponsiveContainer width={160} height={160}>
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={52}
                            outerRadius={72}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                            strokeWidth={0}
                          >
                            {chartData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        {allZero ? (
                          <span className="text-sm text-muted-foreground">—</span>
                        ) : (
                          <>
                            <span className="text-base font-bold leading-tight">{categoryCount}</span>
                            <span className="text-[10px] text-muted-foreground">
                              {categoryCount === 1 ? "Category" : "Categories"}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="w-full space-y-1.5">
                      {allZero ? (
                        <p className="text-xs text-muted-foreground text-center">No invoice line items yet.</p>
                      ) : (
                        data.map((d, i) => (
                          <div key={d.category} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-1.5">
                              <span
                                className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                              />
                              <span className="text-muted-foreground truncate max-w-[120px]">{d.category}</span>
                            </div>
                            <span className="font-medium">{formatCurrency(d.total)}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Outstanding Invoices */}
          <Card className="shadow-sm border-border/50">
            <CardHeader>
              <CardTitle>Outstanding Invoices</CardTitle>
              <CardDescription>Unpaid invoices — overdue ones are highlighted</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {isLoadingSent || isLoadingOverdue ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    </div>
                  ))
                ) : outstanding.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="mx-auto h-8 w-8 text-green-500 mb-2" />
                    <p className="text-sm text-muted-foreground">No outstanding invoices.</p>
                  </div>
                ) : (
                  outstanding.map((invoice) => (
                    <Link key={invoice.id} href={`/invoices/${invoice.id}`}>
                      <div className="flex items-center justify-between p-3 -mx-3 rounded-md hover:bg-muted/50 cursor-pointer transition-colors group">
                        <div className="flex flex-col gap-0.5 min-w-0 pr-3">
                          <span className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                            {invoice.title || invoice.customerName}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {invoice.invoiceNumber ? `#${invoice.invoiceNumber} · ` : ""}
                            {invoice.dueDate ? `Due ${formatDate(invoice.dueDate)}` : "No due date"}
                          </span>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className="text-sm font-semibold">{formatCurrency(invoice.total)}</span>
                          <Badge
                            variant="outline"
                            className={`text-[10px] px-1.5 py-0 ${getInvoiceStatusColor(invoice.status)}`}
                          >
                            {invoice.status === "overdue" ? "Overdue" : invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <Link href="/invoices" className="text-sm text-primary hover:underline font-medium flex items-center justify-center w-full">
                  View all invoices
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
